import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import RankBadge from '../components/RankBadge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Card, CardContent } from '../components/ui/card';
import { 
    CheckCircle, 
    Search, 
    MapPin, 
    Star,
    TrendingUp,
    ArrowRight,
    Sparkles
} from 'lucide-react';
import { addressAPI, authAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const ReviewSuccess = () => {
    const { addressId } = useParams();
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const [address, setAddress] = useState(null);
    const [rankProgress, setRankProgress] = useState(null);

    useEffect(() => {
        loadData();
    }, [addressId]);

    const loadData = async () => {
        try {
            // Load address info
            const addrResponse = await addressAPI.get(addressId);
            setAddress(addrResponse.data);
            
            // Refresh user data to get updated rank
            const userResponse = await authAPI.getMe();
            if (userResponse.data) {
                updateUser(userResponse.data);
                setRankProgress({
                    rank_name: userResponse.data.rank_name,
                    rank_points: userResponse.data.rank_points,
                    points_to_next_rank: userResponse.data.points_to_next_rank,
                    next_rank_name: userResponse.data.next_rank_name,
                    progress_percent: userResponse.data.progress_percent
                });
            }
        } catch (err) {
            console.error('Failed to load data:', err);
        }
    };

    // Points earned per review
    const POINTS_PER_REVIEW = 10;

    return (
        <Layout>
            <div className="min-h-[80vh] flex items-center justify-center px-4 py-8">
                <div className="max-w-md w-full">
                    {/* Success Header */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-600/20 mb-4 animate-pulse">
                            <CheckCircle className="h-12 w-12 text-emerald-500" />
                        </div>
                        <h1 className="font-heading font-bold text-3xl text-foreground" data-testid="success-title">
                            Review Added
                        </h1>
                        <p className="text-muted-foreground mt-2">
                            Thanks for protecting the trades!
                        </p>
                    </div>

                    {/* Rank Progress Card */}
                    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20 mb-6" data-testid="rank-progress-card">
                        <CardContent className="p-6">
                            {/* Points Earned */}
                            <div className="text-center mb-6">
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 rounded-full mb-2">
                                    <Sparkles className="h-5 w-5 text-emerald-500" />
                                    <span className="font-bold text-lg text-emerald-500" data-testid="points-earned">
                                        +{POINTS_PER_REVIEW} Rank Points
                                    </span>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    Earned for this review
                                </p>
                            </div>

                            {/* Rank Display */}
                            {rankProgress && (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-center gap-3">
                                        <RankBadge 
                                            rank={rankProgress.rank_name} 
                                            size="md" 
                                            showGlow={true}
                                        />
                                        <div className="text-left">
                                            <p className="text-xs text-muted-foreground">Current Rank</p>
                                            <p className="font-bold text-lg text-foreground" data-testid="current-rank">
                                                {rankProgress.rank_name}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-xs text-muted-foreground">
                                            <span className="font-medium text-foreground">
                                                {rankProgress.rank_points} pts
                                            </span>
                                            {rankProgress.next_rank_name && (
                                                <span>
                                                    {rankProgress.points_to_next_rank} pts to{' '}
                                                    <span className="font-semibold text-primary">
                                                        {rankProgress.next_rank_name}
                                                    </span>
                                                </span>
                                            )}
                                        </div>
                                        <Progress 
                                            value={rankProgress.progress_percent || 0} 
                                            className="h-3"
                                            data-testid="rank-progress-bar"
                                        />
                                    </div>

                                    {/* Encouragement Message */}
                                    <div className="text-center pt-2">
                                        <p className="text-sm text-muted-foreground">
                                            {rankProgress.next_rank_name ? (
                                                <>
                                                    <TrendingUp className="h-4 w-4 inline mr-1 text-primary" />
                                                    Keep logging jobs to reach <span className="font-semibold text-primary">{rankProgress.next_rank_name}</span>!
                                                </>
                                            ) : (
                                                <>
                                                    <Star className="h-4 w-4 inline mr-1 text-amber-500" />
                                                    You've reached the highest rank. Legend status!
                                                </>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Address Info */}
                    {address && (
                        <div className="flex items-center justify-center gap-2 text-muted-foreground mb-6 px-4 py-3 bg-card/50 border border-border rounded-lg">
                            <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-sm truncate" data-testid="address-display">
                                {address.street_address}, {address.city}
                            </span>
                        </div>
                    )}

                    {/* Action Buttons - Primary is Search Another */}
                    <div className="space-y-3">
                        {/* PRIMARY ACTION: Search Another Address */}
                        <Button 
                            onClick={() => navigate('/dashboard')}
                            className="w-full h-14 text-lg font-semibold"
                            data-testid="search-another-btn"
                        >
                            <Search className="mr-2 h-5 w-5" />
                            Search Another Address
                        </Button>

                        {/* SECONDARY ACTION: View Property */}
                        <Link to={`/property/${addressId}`} className="block">
                            <Button 
                                variant="outline"
                                className="w-full h-12"
                                data-testid="view-property-btn"
                            >
                                View Property
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                    </div>

                    {/* Motivation Footer */}
                    <p className="text-center text-xs text-muted-foreground mt-6">
                        Every review helps contractors make smarter decisions.
                    </p>
                </div>
            </div>
        </Layout>
    );
};

export default ReviewSuccess;
